import { ActionContext, ActionTree, GetterTree, Module, MutationTree } from 'vuex';
import { useToast } from 'vue-toastification';
import { RootState } from '..';
import { ProjectRequest, ProjectSummary, ProjectTypeSummary, ProjectChart, Status } from './project.model';
import projectService from './project.service';

// namespace
export const PROJECT_STORE_NAMESPACE = 'projectStore';

// state
interface ProjectState {
  projectSummary: ProjectSummary | null;
  projectTypeSummaries: ProjectTypeSummary[];
  projectChart: ProjectChart | null;
  status: [Status, Status, Status];
}

const initialState: ProjectState = {
  projectSummary: null,
  projectTypeSummaries: [],
  projectChart: null,
  status: ['init', 'init', 'init']
};

// mutation
const mutationConstant = {
  GET_SUMMARY: 'getSummary',
  GET_TYPE_SUMMARY: 'getTypeSummary',
  GET_CHART: 'getChart',
  PENDING: 'pending',
  FULFILLED: 'fulfilled',
  REJECTED: 'rejected'
};

const mutation: MutationTree<ProjectState> = {
  [mutationConstant.GET_SUMMARY]: (state: ProjectState, projectSummary: ProjectSummary) => {
    state.projectSummary = projectSummary;
  },
  [mutationConstant.GET_TYPE_SUMMARY]: (state: ProjectState, projectTypeSummaries: ProjectTypeSummary[]) => {
    state.projectTypeSummaries = projectTypeSummaries;
  },
  [mutationConstant.GET_CHART]: (state: ProjectState, projectChart: ProjectChart) => {
    state.projectChart = projectChart;
  },
  [mutationConstant.PENDING]: (state: ProjectState, index: number) => {
    state.status[index] = 'loading';
  },
  [mutationConstant.FULFILLED]: (state: ProjectState, index: number) => {
    state.status[index] = 'idle';
  },
  [mutationConstant.REJECTED]: (state: ProjectState, index: number) => {
    state.status[index] = 'failed';
    window.location.href = '/';
  },
};

// action
const toast = useToast();
const actionConstant = {
  GET_SUMMARY: 'getSummary',
  GET_MINISTER_SUMMARY: 'getMinisterSummary',
  GET_REGION_SUMMARY: 'getRegionSummary',
  GET_PROVINCE_SUMMARY: 'getProvinceSummary',
  GET_TYPE_SUMMARY: 'getTypeSummary',
  GET_MINISTER_TYPE_SUMMARY: 'getMinisterTypeSummary',
  GET_REGION_TYPE_SUMMARY: 'getRegionTypeSummary',
  GET_PROVINCE_TYPE_SUMMARY: 'getProvinceTypeSummary',
  GET_CHART: 'getChart',
  GET_MINISTER_CHART: 'getMinisterChart',
  GET_REGION_CHART: 'getRegionChart',
  GET_PROVINCE_CHART: 'getProvinceChart',
};

const action: ActionTree<ProjectState, RootState> = {
  [actionConstant.GET_SUMMARY](context: ActionContext<ProjectState, RootState>, payload: ProjectRequest) {
    return new Promise<void>((resolve, reject) => {
      context.commit(mutationConstant.PENDING, 0);
      projectService.GetSummary(payload.fiscalYearId as number)
        .then((response) => {
          context.commit(mutationConstant.GET_SUMMARY, response.data);
          context.commit(mutationConstant.FULFILLED, 0);
          resolve();
        })
        .catch(() => {
          context.commit(mutationConstant.REJECTED, 0);
          toast.error('ไม่สามารถเรียกข้อมูลได้');
          reject();
        });
    });
  },
  [actionConstant.GET_MINISTER_SUMMARY](context: ActionContext<ProjectState, RootState>, payload: ProjectRequest) {
    return new Promise<void>((resolve, reject) => {
      context.commit(mutationConstant.PENDING, 0);
      projectService.GetMinisterSummary(payload.fiscalYearId as number, payload.ministerId as number)
        .then((response) => {
          context.commit(mutationConstant.GET_SUMMARY, response.data);
          context.commit(mutationConstant.FULFILLED, 0);
          resolve();
        })
        .catch(() => {
          context.commit(mutationConstant.REJECTED, 0);
          toast.error('ไม่สามารถเรียกข้อมูลได้');
          reject();
        });
    });
  },
  [actionConstant.GET_REGION_SUMMARY](context: ActionContext<ProjectState, RootState>, payload: ProjectRequest) {
    return new Promise<void>((resolve, reject) => {
      context.commit(mutationConstant.PENDING, 0);
      projectService.GetRegionSummary(payload.fiscalYearId as number, payload.regionId as number)
        .then((response) => {
          context.commit(mutationConstant.GET_SUMMARY, response.data);
          context.commit(mutationConstant.FULFILLED, 0);
          resolve();
        })
        .catch(() => {
          context.commit(mutationConstant.REJECTED, 0);
          toast.error('ไม่สามารถเรียกข้อมูลได้');
          reject();
        });
    });
  },
  [actionConstant.GET_PROVINCE_SUMMARY](context: ActionContext<ProjectState, RootState>, payload: ProjectRequest) {
    return new Promise<void>((resolve, reject) => {
      context.commit(mutationConstant.PENDING, 0);
      projectService.GetProvinceSummary(payload.fiscalYearId as number, payload.provinceId as number)
        .then((response) => {
          context.commit(mutationConstant.GET_SUMMARY, response.data);
          context.commit(mutationConstant.FULFILLED, 0);
          resolve();
        })
        .catch(() => {
          context.commit(mutationConstant.REJECTED, 0);
          toast.error('ไม่สามารถเรียกข้อมูลได้');
          reject();
        });
    });
  },
  [actionConstant.GET_TYPE_SUMMARY](context: ActionContext<ProjectState, RootState>, payload: ProjectRequest) {
    return new Promise<void>((resolve, reject) => {
      context.commit(mutationConstant.PENDING, 1);
      projectService.GetTypeSummary(payload.fiscalYearId as number)
        .then((response) => {
          context.commit(mutationConstant.GET_TYPE_SUMMARY, response.data);
          context.commit(mutationConstant.FULFILLED, 1);
          resolve();
        })
        .catch(() => {
          context.commit(mutationConstant.REJECTED, 1);
          toast.error('ไม่สามารถเรียกข้อมูลได้');
          reject();
        });
    });
  },
  [actionConstant.GET_MINISTER_TYPE_SUMMARY](context: ActionContext<ProjectState, RootState>, payload: ProjectRequest) {
    return new Promise<void>((resolve, reject) => {
      context.commit(mutationConstant.PENDING, 1);
      projectService.GetMinisterTypeSummary(payload.fiscalYearId as number, payload.ministerId as number)
        .then((response) => {
          context.commit(mutationConstant.GET_TYPE_SUMMARY, response.data);
          context.commit(mutationConstant.FULFILLED, 1);
          resolve();
        })
        .catch(() => {
          context.commit(mutationConstant.REJECTED, 1);
          toast.error('ไม่สามารถเรียกข้อมูลได้');
          reject();
        });
    });
  },
  [actionConstant.GET_REGION_TYPE_SUMMARY](context: ActionContext<ProjectState, RootState>, payload: ProjectRequest) {
    return new Promise<void>((resolve, reject) => {
      context.commit(mutationConstant.PENDING, 1);
      projectService.GetRegionTypeSummary(payload.fiscalYearId as number, payload.regionId as number)
        .then((response) => {
          context.commit(mutationConstant.GET_TYPE_SUMMARY, response.data);
          context.commit(mutationConstant.FULFILLED, 1);
          resolve();
        })
        .catch(() => {
          context.commit(mutationConstant.REJECTED, 1);
          toast.error('ไม่สามารถเรียกข้อมูลได้');
          reject();
        });
    });
  },
  [actionConstant.GET_PROVINCE_TYPE_SUMMARY](context: ActionContext<ProjectState, RootState>, payload: ProjectRequest) {
    return new Promise<void>((resolve, reject) => {
      context.commit(mutationConstant.PENDING, 1);
      projectService.GetProvinceTypeSummary(payload.fiscalYearId as number, payload.provinceId as number)
        .then((response) => {
          context.commit(mutationConstant.GET_TYPE_SUMMARY, response.data);
          context.commit(mutationConstant.FULFILLED, 1);
          resolve();
        })
        .catch(() => {
          context.commit(mutationConstant.REJECTED, 1);
          toast.error('ไม่สามารถเรียกข้อมูลได้');
          reject();
        });
    });
  },
  [actionConstant.GET_CHART](context: ActionContext<ProjectState, RootState>, payload: ProjectRequest) {
    return new Promise<void>((resolve, reject) => {
      context.commit(mutationConstant.PENDING, 2);
      projectService.GetChart(payload.fiscalYearId as number)
        .then((response) => {
          context.commit(mutationConstant.GET_CHART, response.data);
          context.commit(mutationConstant.FULFILLED, 2);
          resolve();
        })
        .catch(() => {
          context.commit(mutationConstant.REJECTED, 2);
          toast.error('ไม่สามารถเรียกข้อมูลได้');
          reject();
        });
    });
  },
  [actionConstant.GET_MINISTER_CHART](context: ActionContext<ProjectState, RootState>, payload: ProjectRequest) {
    return new Promise<void>((resolve, reject) => {
      context.commit(mutationConstant.PENDING, 2);
      projectService.GetMinisterChart(payload.fiscalYearId as number, payload.ministerId as number)
        .then((response) => {
          context.commit(mutationConstant.GET_CHART, response.data);
          context.commit(mutationConstant.FULFILLED, 2);
          resolve();
        })
        .catch(() => {
          context.commit(mutationConstant.REJECTED, 2);
          toast.error('ไม่สามารถเรียกข้อมูลได้');
          reject();
        });
    });
  },
  [actionConstant.GET_REGION_CHART](context: ActionContext<ProjectState, RootState>, payload: ProjectRequest) {
    return new Promise<void>((resolve, reject) => {
      context.commit(mutationConstant.PENDING, 2);
      projectService.GetRegionChart(payload.fiscalYearId as number, payload.regionId as number)
        .then((response) => {
          context.commit(mutationConstant.GET_CHART, response.data);
          context.commit(mutationConstant.FULFILLED, 2);
          resolve();
        })
        .catch(() => {
          context.commit(mutationConstant.REJECTED, 2);
          toast.error('ไม่สามารถเรียกข้อมูลได้');
          reject();
        });
    });
  },
  [actionConstant.GET_PROVINCE_CHART](context: ActionContext<ProjectState, RootState>, payload: ProjectRequest) {
    return new Promise<void>((resolve, reject) => {
      context.commit(mutationConstant.PENDING, 2);
      projectService.GetProvinceChart(payload.fiscalYearId as number, payload.provinceId as number)
        .then((response) => {
          context.commit(mutationConstant.GET_CHART, response.data);
          context.commit(mutationConstant.FULFILLED, 2);
          resolve();
        })
        .catch(() => {
          context.commit(mutationConstant.REJECTED);
          toast.error('ไม่สามารถเรียกข้อมูลได้');
          reject();
        });
    });
  },
};

// getter
const getterConstant = {
  SUMMARY: 'summary',
  TYPE_SUMMARY: 'typeSummary',
  CHART: 'chart',
  SUMMARY_STATUS: 'summaryStatus',
  TYPE_SUMMARY_STATUS: 'typeSummaryStatus',
  CHART_STATUS: 'chartStatus',
};

const getter: GetterTree<ProjectState, RootState> = {
  [getterConstant.SUMMARY]: (state: ProjectState) => state.projectSummary,
  [getterConstant.TYPE_SUMMARY]: (state: ProjectState) => state.projectTypeSummaries,
  [getterConstant.CHART]: (state: ProjectState) => state.projectChart,
  [getterConstant.SUMMARY_STATUS]: (state: ProjectState) => state.status[0],
  [getterConstant.TYPE_SUMMARY_STATUS]: (state: ProjectState) => state.status[1],
  [getterConstant.CHART_STATUS]: (state: ProjectState) => state.status[2],
};

// module
const module: Module<ProjectState, RootState> = {
  namespaced: true,
  state: initialState,
  mutations: mutation,
  actions: action,
  getters: getter
};

// export
export const ProjectGetter = {
  SUMMARY: `${PROJECT_STORE_NAMESPACE}/${getterConstant.SUMMARY}`,
  TYPE_SUMMARY: `${PROJECT_STORE_NAMESPACE}/${getterConstant.TYPE_SUMMARY}`,
  CHART: `${PROJECT_STORE_NAMESPACE}/${getterConstant.CHART}`,
  SUMMARY_STATUS: `${PROJECT_STORE_NAMESPACE}/${getterConstant.SUMMARY_STATUS}`,
  TYPE_SUMMARY_STATUS: `${PROJECT_STORE_NAMESPACE}/${getterConstant.TYPE_SUMMARY_STATUS}`,
  CHART_STATUS: `${PROJECT_STORE_NAMESPACE}/${getterConstant.CHART_STATUS}`
};

export const ProjectAction = {
  GET_SUMMARY: `${PROJECT_STORE_NAMESPACE}/${actionConstant.GET_SUMMARY}`,
  GET_MINISTER_SUMMARY: `${PROJECT_STORE_NAMESPACE}/${actionConstant.GET_MINISTER_SUMMARY}`,
  GET_REGION_SUMMARY: `${PROJECT_STORE_NAMESPACE}/${actionConstant.GET_REGION_SUMMARY}`,
  GET_PROVINCE_SUMMARY: `${PROJECT_STORE_NAMESPACE}/${actionConstant.GET_PROVINCE_SUMMARY}`,
  GET_TYPE_SUMMARY: `${PROJECT_STORE_NAMESPACE}/${actionConstant.GET_TYPE_SUMMARY}`,
  GET_MINISTER_TYPE_SUMMARY: `${PROJECT_STORE_NAMESPACE}/${actionConstant.GET_MINISTER_TYPE_SUMMARY}`,
  GET_REGION_TYPE_SUMMARY: `${PROJECT_STORE_NAMESPACE}/${actionConstant.GET_REGION_TYPE_SUMMARY}`,
  GET_PROVINCE_TYPE_SUMMARY: `${PROJECT_STORE_NAMESPACE}/${actionConstant.GET_PROVINCE_TYPE_SUMMARY}`,
  GET_CHART: `${PROJECT_STORE_NAMESPACE}/${actionConstant.GET_CHART}`,
  GET_MINISTER_CHART: `${PROJECT_STORE_NAMESPACE}/${actionConstant.GET_MINISTER_CHART}`,
  GET_REGION_CHART: `${PROJECT_STORE_NAMESPACE}/${actionConstant.GET_REGION_CHART}`,
  GET_PROVINCE_CHART: `${PROJECT_STORE_NAMESPACE}/${actionConstant.GET_PROVINCE_CHART}`,
};

export default module;
